import type { ReactElement, ReactNode } from 'react'
import { type CompanyId, isNotEmpty } from '@terros/common'
import { Link } from '@tanstack/react-router'
import { Group, Skeleton, Text } from '@mantine/core'
import { getInitialism, useCompany } from '@front/model'
import { AvatarBlurhash, type AvatarSize, getSize } from './AvatarBlurhash'

type CompanyInfoFetcherInput = {
  size?: AvatarSize
  avatarOnly?: boolean
  companyId?: CompanyId
  linked?: boolean
  color?: string
}

type CompanyInfoPrefetchedInput = CompanyInfoFetcherInput & {
  avatarUrl?: string
  avatarBlurhash?: string
  initials: string
  name: string
}

export const CompanyInfo = ({
  size = 'sm',
  avatarOnly,
  companyId,
  linked,
  color,
}: CompanyInfoFetcherInput): ReactNode => {
  const { data, isLoading, error } = useCompany({ companyId })
  if (error) return <Text className='text-red-400'>{error}</Text>
  if (isLoading || !data)
    return avatarOnly ? <Skeleton circle h={getSize(size)} w={getSize(size)} /> : <Skeleton h={20} w={200} />
  return (
    <PrefetchedCompanyInfo
      avatarBlurhash={data.avatarBlurhash}
      avatarOnly={avatarOnly}
      avatarUrl={data.avatarUrl}
      companyId={companyId}
      color={color}
      initials={getInitialism(data.name)}
      linked={linked}
      name={data.name}
      size={size}
    />
  )
}

export const PrefetchedCompanyInfo = (props: CompanyInfoPrefetchedInput): ReactElement => {
  const { size = 'sm', avatarUrl, avatarBlurhash, initials, name, avatarOnly, companyId, linked = true, color } = props
  const hasBackgroundColor = isNotEmpty(color)
  if (linked) {
    return (
      <Link to={`/companies/${companyId}`}>
        <PrefetchedCompanyInfo {...props} linked={false} />
      </Link>
    )
  }
  return (
    <Group wrap='nowrap'>
      <AvatarBlurhash
        avatarBlurhash={avatarBlurhash}
        avatarUrl={avatarUrl}
        color={color}
        initials={initials}
        noBlurhash={hasBackgroundColor}
        size={size}
        styles={hasBackgroundColor ? { root: { backgroundColor: color } } : undefined}
      />
      {!avatarOnly && <Text lineClamp={1}>{name}</Text>}
    </Group>
  )
}
